import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Edit2, ArrowRight } from 'lucide-react';
import { CardSectionProps } from '../../../types/compositeCards';
import { PrimeArtifact, PrimeArtifactState } from '../../../types/primeModel';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import styles from './PrimeStatesContextCard.module.css';

interface PrimeStatesContextCardProps extends CardSectionProps {}

export const PrimeStatesContextCard: React.FC<PrimeStatesContextCardProps> = ({ card, onAction }) => {
  const [states, setStates] = useState<PrimeArtifactState[]>([]);
  const [loading, setLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  // Extract prime artifact data
  const data = card.data as any;
  const primeArtifact: PrimeArtifact = data.primeArtifact || data.artifact;
  const isArchitect = card.context.uiRole === 'architect';

  useEffect(() => {
    const loadStates = async () => {
      if (!primeArtifact?.id) return;
      
      setLoading(true);
      try {
        const artifact = await PrimeArtifactService.getById(primeArtifact.id);
        setStates(artifact.states || []);
        setIsApproved(artifact.states_approved || false);
      } catch (error) {
        console.error('Failed to load prime states:', error);
      } finally {
        setLoading(false);
      }
    };

    void loadStates();
  }, [primeArtifact?.id]);

  const handleToggleApproval = async () => {
    if (!primeArtifact?.id) return;
    
    try {
      if (isApproved) {
        await PrimeArtifactService.rejectStates(primeArtifact.id);
        setIsApproved(false);
      } else {
        await PrimeArtifactService.approveStates(primeArtifact.id);
        setIsApproved(true);
      }
    } catch (error) {
      console.error('Failed to toggle states approval:', error);
    }
  };

  const handleEditStates = () => {
    onAction?.('edit-states', { primeArtifact });
  };

  if (loading) {
    return <div className={styles.primeStatesContextLoading}>Loading states...</div>;
  }

  return (
    <div className={styles.primeStatesContextCard}>
      <div className={styles.primeStatesHeader}>
        <h3>Prime States</h3>
        {isArchitect && (
          <div className={styles.primeStatesActions}>
            <button
              className={`${styles.approvalToggle} ${isApproved ? styles.approved : styles.notApproved}`}
              onClick={handleToggleApproval}
            >
              {isApproved ? <CheckCircle size={16} /> : <XCircle size={16} />}
              {isApproved ? 'Approved' : 'Not Approved'}
            </button>
          </div>
        )}
      </div>

      {states.length === 0 ? (
        <div className={styles.noStatesMessage}>
          <p>No states defined yet</p>
          {isArchitect && (
            <button className={styles.editStatesButton} onClick={handleEditStates}>
              <Edit2 size={14} />
              Add States
            </button>
          )}
        </div>
      ) : (
        <div className={styles.statesList}>
          {states.map((state, index) => (
            <div key={state.id} className={styles.stateItem}>
              <div className={styles.stateOrder}>{index + 1}</div>
              <div className={styles.stateContent}>
                <div className={styles.stateName}>{state.name}</div>
                {state.description && (
                  <div className={styles.stateDescription}>{state.description}</div>
                )}
              </div>
              <div className={styles.stateStatus}>
                {state.is_final && (
                  <span className={styles.terminalBadge}>Final</span>
                )}
                {state.is_initial && (
                  <span className={styles.initialBadge}>Initial</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isArchitect && states.length > 0 && (
        <div className={styles.primeStatesFooter}>
          <button className={styles.editStatesButton} onClick={handleEditStates}>
            <Edit2 size={14} />
            Edit States
          </button>
        </div>
      )}
    </div>
  );
};

